"use client";

import React, { useEffect, useState } from 'react';
import { Calendar, Plus, Users, Clock, MoreVertical, Edit2, Trash2, Save, X, MapPin, Target } from 'lucide-react';
import { supabase } from '../../integrations/supabase/client';

interface Workshop {
  id: string;
  title: string;
  description?: string;
  date: string;
  time?: string;
  duration?: number;
  location?: string;
  client_id?: string;
  status: string;
  template_used?: string;
  max_participants?: number;
  current_participants?: number;
  created_at: string;
  updated_at: string;
}

interface Client {
  id: string;
  name: string;
  media_name: string;
}

const Workshops = () => {
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingWorkshop, setEditingWorkshop] = useState<Workshop | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    fetchWorkshops();
    fetchClients();
    
    // Set up real-time subscription
    const subscription = supabase
      .channel('workshops-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'workshops' },
        () => fetchWorkshops()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    filterWorkshops();
  }, [workshops, statusFilter]);

  const fetchWorkshops = async () => {
    try {
      const { data, error } = await supabase
        .from('workshops')
        .select('*, clients(name, media_name)')
        .order('date', { ascending: true });
      
      if (!error && data) setWorkshops(data);
    } catch (error) {
      console.error('Error fetching workshops:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('id, name, media_name')
        .eq('status', 'active')
        .order('media_name');
      
      if (!error && data) setClients(data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const filterWorkshops = () => {
    if (statusFilter === 'all') {
      return workshops;
    }
    return workshops.filter(workshop => workshop.status === statusFilter);
  };

  const handleSaveWorkshop = async (workshop: Workshop) => {
    try {
      const workshopData = {
        title: workshop.title,
        description: workshop.description,
        date: workshop.date,
        time: workshop.time,
        duration: workshop.duration,
        location: workshop.location,
        client_id: workshop.client_id,
        status: workshop.status,
        template_used: workshop.template_used,
        max_participants: workshop.max_participants,
        current_participants: workshop.current_participants
      };

      if (editingWorkshop?.id) {
        // Update existing workshop
        const { error } = await supabase
          .from('workshops')
          .update(workshopData)
          .eq('id', workshop.id);

        if (error) throw error;
      } else {
        // Create new workshop
        const { error } = await supabase
          .from('workshops')
          .insert(workshopData);

        if (error) throw error;
      }

      setEditingWorkshop(null);
      setShowAddModal(false);
    } catch (error) {
      console.error('Error saving workshop:', error);
    }
  };

  const handleDeleteWorkshop = async (workshopId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este workshop?')) return;
    
    try {
      const { error } = await supabase
        .from('workshops')
        .delete()
        .eq('id', workshopId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting workshop:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-50 text-blue-600';
      case 'completed': return 'bg-green-50 text-green-600';
      case 'cancelled': return 'bg-red-50 text-red-600';
      default: return 'bg-gray-50 text-gray-600';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'scheduled': return 'Programado';
      case 'completed': return 'Completado';
      case 'cancelled': return 'Cancelado';
      default: return status;
    }
  };

  const filteredWorkshops = filterWorkshops();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-charcoal">Workshops</h1>
          <p className="text-charcoal-muted">Gestión de capacitaciones y eventos.</p>
        </div>
        <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="all">Todos los estados</option>
            <option value="scheduled">Programados</option>
            <option value="completed">Completados</option>
            <option value="cancelled">Cancelados</option>
          </select>
          <button 
            onClick={() => setShowAddModal(true)}
            className="btn-primary"
          >
            <Plus size={20} /> Nuevo Workshop
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredWorkshops.length > 0 ? filteredWorkshops.map((workshop) => (
          <div key={workshop.id} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all card-hover">
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 bg-purple-50 text-purple-600 rounded-2xl">
                <Calendar size={24} />
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setEditingWorkshop(workshop)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Edit2 size={16} className="text-gray-400" />
                </button>
                <button 
                  onClick={() => handleDeleteWorkshop(workshop.id)}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={16} className="text-red-400" />
                </button>
              </div>
            </div>
            
            <h3 className="text-lg font-bold text-charcoal mb-2">{workshop.title}</h3>
            {workshop.description && (
              <p className="text-sm text-charcoal-muted mb-4 line-clamp-2">{workshop.description}</p>
            )}
            
            <div className="space-y-3 mb-4">
              <div className="flex items-center gap-2 text-sm text-charcoal-muted">
                <Calendar size={16} />
                <span>{new Date(workshop.date).toLocaleDateString('es-DO', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
              
              {workshop.time && (
                <div className="flex items-center gap-2 text-sm text-charcoal-muted">
                  <Clock size={16} />
                  <span>{workshop.time}</span>
                </div>
              )}
              
              {workshop.location && (
                <div className="flex items-center gap-2 text-sm text-charcoal-muted">
                  <MapPin size={16} />
                  <span>{workshop.location}</span>
                </div>
              )}
              
              {workshop.max_participants && (
                <div className="flex items-center gap-2 text-sm text-charcoal-muted">
                  <Users size={16} />
                  <span>{workshop.current_participants || 0}/{workshop.max_participants} participantes</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-50">
              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest ${getStatusColor(workshop.status)}`}>
                {getStatusText(workshop.status)}
              </span>
              
              {workshop.clients && (
                <div className="text-right">
                  <p className="text-xs text-charcoal-muted">Cliente</p>
                  <p className="text-sm font-medium text-charcoal">{workshop.clients.media_name}</p>
                </div>
              )}
            </div>
          </div>
        )) : (
          <div className="col-span-full flex flex-col items-center justify-center py-12 text-charcoal-muted">
            <Calendar size={48} className="mb-4 opacity-20" />
            <p>No se encontraron workshops</p>
          </div>
        )}
      </div>

      {/* Add/Edit Workshop Modal */}
      {(showAddModal || editingWorkshop) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-charcoal">
                {editingWorkshop ? 'Editar Workshop' : 'Nuevo Workshop'}
              </h2>
              <button 
                onClick={() => {
                  setEditingWorkshop(null);
                  setShowAddModal(false);
                }}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-charcoal mb-2">Título</label>
                <input
                  type="text"
                  value={editingWorkshop?.title || ''}
                  onChange={(e) => setEditingWorkshop(editingWorkshop ? {...editingWorkshop, title: e.target.value} : {
                    id: '',
                    title: e.target.value,
                    status: 'scheduled',
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-charcoal mb-2">Descripción</label>
                <textarea
                  value={editingWorkshop?.description || ''}
                  onChange={(e) => setEditingWorkshop(editingWorkshop ? {...editingWorkshop, description: e.target.value} : {
                    id: '',
                    title: '',
                    description: e.target.value,
                    status: 'scheduled',
                    created_at: '',
                    updated_at: ''
                  })}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Fecha</label>
                <input
                  type="date"
                  value={editingWorkshop?.date || ''}
                  onChange={(e) => setEditingWorkshop(editingWorkshop ? {...editingWorkshop, date: e.target.value} : {
                    id: '',
                    title: '',
                    date: e.target.value,
                    status: 'scheduled',
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Hora</label>
                <input
                  type="time"
                  value={editingWorkshop?.time || ''}
                  onChange={(e) => setEditingWorkshop(editingWorkshop ? {...editingWorkshop, time: e.target.value} : {
                    id: '',
                    title: '',
                    time: e.target.value,
                    status: 'scheduled',
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Duración (minutos)</label>
                <input
                  type="number"
                  value={editingWorkshop?.duration || ''}
                  onChange={(e) => setEditingWorkshop(editingWorkshop ? {...editingWorkshop, duration: Number(e.target.value)} : {
                    id: '',
                    title: '',
                    duration: Number(e.target.value),
                    status: 'scheduled',
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Ubicación</label>
                <input
                  type="text"
                  value={editingWorkshop?.location || ''}
                  onChange={(e) => setEditingWorkshop(editingWorkshop ? {...editingWorkshop, location: e.target.value} : {
                    id: '',
                    title: '',
                    location: e.target.value,
                    status: 'scheduled',
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Cliente</label>
                <select
                  value={editingWorkshop?.client_id || ''}
                  onChange={(e) => setEditingWorkshop(editingWorkshop ? {...editingWorkshop, client_id: e.target.value} : {
                    id: '',
                    title: '',
                    client_id: e.target.value,
                    status: 'scheduled',
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">Seleccionar cliente</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.media_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Estado</label>
                <select
                  value={editingWorkshop?.status || 'scheduled'}
                  onChange={(e) => setEditingWorkshop(editingWorkshop ? {...editingWorkshop, status: e.target.value} : {
                    id: '',
                    title: '',
                    status: e.target.value,
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="scheduled">Programado</option>
                  <option value="completed">Completado</option>
                  <option value="cancelled">Cancelado</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Máximo de Participantes</label>
                <input
                  type="number"
                  value={editingWorkshop?.max_participants || ''}
                  onChange={(e) => setEditingWorkshop(editingWorkshop ? {...editingWorkshop, max_participants: Number(e.target.value)} : {
                    id: '',
                    title: '',
                    max_participants: Number(e.target.value),
                    status: 'scheduled',
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-charcoal mb-2">Template Utilizado</label>
                <input
                  type="text"
                  value={editingWorkshop?.template_used || ''}
                  onChange={(e) => setEditingWorkshop(editingWorkshop ? {...editingWorkshop, template_used: e.target.value} : {
                    id: '',
                    title: '',
                    template_used: e.target.value,
                    status: 'scheduled',
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => {
                  setEditingWorkshop(null);
                  setShowAddModal(false);
                }}
                className="flex-1 px-6 py-3 border border-gray-200 text-charcoal rounded-2xl font-bold hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => editingWorkshop && handleSaveWorkshop(editingWorkshop)}
                className="flex-1 btn-primary"
              >
                <Save size={20} /> {editingWorkshop ? 'Guardar Cambios' : 'Crear Workshop'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Workshops;